import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { lgSurface, lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { fluid } from '../styles/motion';
import { usePresence } from '../hooks/usePresence';
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid';

/*
 * [MYSALES-REPORT-PICK 2026-10-28, user] "When I click on the download button, and then click on the Excel or on the PDF,
 * then I should get a pop-up in the middle of the page with the names, and I can select whatever names I want in the
 * report" (to leave people like Amaan Sofi, Diksha, Hamza out). A pop-up in the middle of the page: every sales rep the
 * file can carry, each with a tick box, a search, Select all / Clear, and Download. Only the ticked reps go in the file.
 * Laptop: three columns of names; phones: one column that scrolls, the buttons always in view. The look and motion of
 * the app's other pop-ups (ConfirmDialog: the same shade, glass and spring).
 */

interface Props {
  open: boolean;
  title: string;              // "Download Excel" / "Download PDF"
  period: string;             // "Apr – Sep 2026"
  icon: string;               // Font Awesome classes for the file type
  tone: 'green' | 'red';
  names: string[];            // the reps the file can carry, A → Z
  initialSelected: string[];  // ticked when it opens
  busy: boolean;              // the file is being made
  error: string | null;
  onCancel: () => void;
  onConfirm: (selected: string[]) => void;
}

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.35)' : 'rgba(20, 30, 25, 0.18)')};
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  ${(p) => fluidScrim(p.$open)}
`;

const Modal = styled.div<{ $open: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 660px;
  max-width: calc(100vw / var(--app-zoom, 1) - 24px); /* [APP-FIT 2026-10-26] the window in page px */
  max-height: calc(88vh / var(--app-zoom, 1));
  padding: 22px 22px 18px;
  box-sizing: border-box;
  border-radius: 20px;
  isolation: isolate;
  color: ${(p) => p.theme.text};
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(230, 233, 231, 0.68) 100%)')};
  backdrop-filter: blur(48px) saturate(180%);
  -webkit-backdrop-filter: blur(48px) saturate(180%);
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.65)')};
  box-shadow:
    0 30px 80px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0, 0, 0, 0.22)')},
    0 8px 24px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0, 0, 0, 0.12)')},
    inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)')};
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 4) : '')}

  @media (max-width: 768px) {
    padding: 18px 14px 14px;
    border-radius: 16px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  ${lgSurface(36)}
  border-color: transparent;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const IconTile = styled.span<{ $tone: 'green' | 'red' }>`
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${(p) => (p.$tone === 'green'
    ? (p.theme.colorScheme === 'dark' ? '#4ade80' : '#15803d')
    : (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626'))};
  background: ${(p) => (p.$tone === 'green'
    ? (p.theme.colorScheme === 'dark' ? 'rgba(74, 222, 128, 0.14)' : 'rgba(21, 128, 61, 0.10)')
    : (p.theme.colorScheme === 'dark' ? 'rgba(248, 113, 113, 0.14)' : 'rgba(220, 38, 38, 0.09)'))};
`;

const HeadText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text)};
`;

const Sub = styled.div`
  font-size: 12.5px;
  line-height: 1.4;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.72)' : p.theme.dim)};
`;

const Tools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Search = styled.input`
  flex: 1 1 200px;
  min-width: 0;
  height: 32px;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 13px;
  padding: 0 11px;
  border-radius: 9px;
  border: 1px solid ${(p) => p.theme.line};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.75)')};
  color: ${(p) => p.theme.text};
  outline: none;
  &::placeholder { color: ${(p) => p.theme.dim}; }
  &:focus { border-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)}; }

  @media (max-width: 768px) {
    flex-basis: 100%;
    height: 38px;
    font-size: 16px; /* no page zoom on focus in iOS */
  }
`;

const SmallBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonSecondary}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */

  @media (max-width: 768px) {
    flex: 1 1 0;
    height: 36px;
  }
`;

/* The names: three columns on a laptop, one on phones; the list scrolls inside the pop-up. */
const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-content: start;
  gap: 2px 8px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(22, 25, 23, 0.08)')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.45)')};

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

/* [MYSALES-REPORT-NO-TIP 2026-10-30, user] "no need of the tool tip here": a row has no title, so no GlobalTooltip bubble. */
const NameRow = styled.label<{ $on: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 34px;
  padding: 4px 9px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(p) => (p.$on ? 600 : 500)};
  color: ${(p) => (p.$on ? p.theme.text : p.theme.dim)};
  &:hover { background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.06)' : p.theme.buttonHover)}; }

  input {
    flex: none;
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
    accent-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  }
  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    min-height: 42px;
    font-size: 14px;
    input { width: 18px; height: 18px; }
  }
`;

const Empty = styled.div`
  grid-column: 1 / -1;
  padding: 18px 8px;
  text-align: center;
  font-size: 13px;
  color: ${(p) => p.theme.dim};
`;

const ErrorLine = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : '#b91c1c')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(248, 113, 113, 0.10)' : 'rgba(220, 38, 38, 0.06)')};
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const Count = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.72)' : p.theme.dim)};

  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const Btn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
  height: 32px; padding: 0 14px; /* [LIQUID-GLASS 2026-11-02, user] the glass capsule at the old size */
  min-width: 96px;

  @media (max-width: 768px) {
    flex: 1 1 0;
    height: 40px;
  }
`;

const ReportPeopleDialog: React.FC<Props> = ({ open, title, period, icon, tone, names, initialSelected, busy, error, onCancel, onConfirm }) => {
  const [sel, setSel] = useState<Set<string>>(() => new Set(initialSelected));
  const [q, setQ] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const pres = usePresence(open, fluid.dialogOutMs);
  const shown = open && pres.entered;

  // Each opening starts from the ticks it was given, with an empty search (React's "adjust state when a prop changes").
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setSel(new Set(initialSelected)); setQ(''); }
  }

  // While it closes, it keeps showing what it showed (the page clears its data at the moment of closing).
  const view = { title, period, icon, tone, names };
  const lastView = useRef(view);
  if (open) lastView.current = view;
  const v = open ? view : lastView.current;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  // The search box takes the keys at once on a laptop; not on touch screens, where it would pop the keyboard up.
  useLayoutEffect(() => {
    if (!open || !pres.mounted) return undefined;
    const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    if (touch) return undefined;
    const id = requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [open, pres.mounted]);

  const shownNames = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? v.names.filter((n) => n.toLowerCase().includes(t)) : v.names;
  }, [q, v.names]);
  const picked = v.names.filter((n) => sel.has(n));
  const toggle = (n: string) => setSel((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  if (!pres.mounted) return null;
  return createPortal(
    <Overlay
      $open={shown}
      data-reload-block={open ? '' : undefined}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
      aria-hidden={!open}
      inert={!open}
    >
      <Modal $open={shown} role="dialog" aria-modal="true" aria-label={v.title}>
        <Head>
          <IconTile $tone={v.tone} aria-hidden="true"><i className={v.icon} /></IconTile>
          <HeadText>
            <Title>{v.title}</Title>
            <Sub>Tick the sales reps to put in the file · {v.period}</Sub>
          </HeadText>
        </Head>
        <Tools>
          <Search
            ref={searchRef}
            type="search"
            placeholder="Search a name"
            aria-label="Search a name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <SmallBtn type="button" onClick={() => setSel(new Set(v.names))} disabled={busy}>Select all</SmallBtn>
          <SmallBtn type="button" onClick={() => setSel(new Set())} disabled={busy}>Clear</SmallBtn>
        </Tools>
        <List role="group" aria-label="Sales reps">
          {shownNames.length === 0 ? (
            <Empty>No name matches “{q.trim()}”.</Empty>
          ) : shownNames.map((n) => (
            <NameRow key={n} $on={sel.has(n)}>
              <input type="checkbox" checked={sel.has(n)} onChange={() => toggle(n)} disabled={busy} />
              <span>{n}</span>
            </NameRow>
          ))}
        </List>
        {error && <ErrorLine role="alert">{error}</ErrorLine>}
        <Foot>
          <Count aria-live="polite">{picked.length} of {v.names.length} sales reps ticked</Count>
          <Btn type="button" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn type="button" $primary onClick={() => onConfirm(picked)} disabled={busy || picked.length === 0}>
            {busy ? 'Preparing…' : 'Download'}
          </Btn>
        </Foot>
      </Modal>
    </Overlay>,
    document.body,
  );
};

export default ReportPeopleDialog;
